import { useState } from 'react'
import { motion } from 'framer-motion'
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react'
import { company } from '@data/company'
import AnimatedSection from '@components/AnimatedSection'

export default function ContactPage() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    if (!formState.name.trim()) newErrors.name = 'Name is required'
    if (!formState.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = 'Please enter a valid email'
    }
    if (!formState.message.trim()) newErrors.message = 'Message is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      const endpoint = import.meta.env.VITE_FORM_ENDPOINT
      if (!endpoint) throw new Error('Form endpoint not configured')

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formState),
      })

      if (!res.ok) throw new Error(`Server error: ${res.status}`)

      setIsSubmitted(true)
      setFormState({ name: '', email: '', company: '', service: '', message: '' })
    } catch {
      setErrors((prev: Record<string, string>) => ({
        ...prev,
        form: 'Submission failed. Please try again or email us directly.',
      }))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormState(prev => ({ ...prev, [e.target.name]: e.target.value }))
    if (errors[e.target.name]) {
      setErrors(prev => { const next = { ...prev }; delete next[e.target.name]; return next })
    }
  }

  return (
    <>
      {/* Page Header */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-martimma-red/10 to-transparent" />
        <div className="section-padding max-w-7xl mx-auto relative z-10">
          <AnimatedSection>
            <span className="text-martimma-red text-sm font-semibold tracking-wider uppercase">Contact Us</span>
            <h1 className="heading-xl mt-4 mb-6">Let's Build the Future Together</h1>
            <p className="body-lg max-w-3xl">
              Ready to partner with Martimma? Reach out to our team for inquiries about our services, partnerships, or career opportunities.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 relative">
        <div className="section-padding max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-8">
              <AnimatedSection>
                <h2 className="heading-md text-white mb-6">Get in Touch</h2>
                <div className="space-y-6">
                  {/* Offices */}
                  {company.offices.map((office) => (
                    <div key={office.city} className="glass-card p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-martimma-red/10 flex items-center justify-center shrink-0">
                          <MapPin className="w-5 h-5 text-martimma-red" />
                        </div>
                        <div>
                          <p className="text-martimma-yellow font-medium text-sm">{office.type}</p>
                          <p className="text-white font-semibold">{office.city}, {office.state}</p>
                          <p className="text-gray-400 text-sm mt-1">{office.address}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Phone */}
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-martimma-yellow/10 flex items-center justify-center shrink-0">
                        <Phone className="w-5 h-5 text-martimma-yellow" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Phone</p>
                        <a href={`tel:${company.contact.phone}`} className="text-white hover:text-martimma-yellow transition-colors">
                          {company.contact.phone}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-martimma-red/10 flex items-center justify-center shrink-0">
                        <Mail className="w-5 h-5 text-martimma-red" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Email</p>
                        <a href={`mailto:${company.contact.email}`} className="text-white hover:text-martimma-yellow transition-colors">
                          {company.contact.email}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-martimma-yellow/10 flex items-center justify-center shrink-0">
                        <Clock className="w-5 h-5 text-martimma-yellow" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Business Hours</p>
                        <p className="text-white">Monday — Friday: 8:00 AM — 6:00 PM</p>
                        <p className="text-gray-400 text-sm">Terminal Operations: 24/7</p>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <AnimatedSection delay={0.2}>
                <div className="glass-card p-8 md:p-10">
                  {isSubmitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                    >
                      <CheckCircle className="w-16 h-16 text-martimma-yellow mx-auto mb-6" />
                      <h3 className="text-2xl font-bold text-white mb-4">Message Sent!</h3>
                      <p className="text-gray-400 mb-8">
                        Thank you for reaching out. Our team will get back to you within 24 hours.
                      </p>
                      <button
                        onClick={() => setIsSubmitted(false)}
                        className="btn-outline"
                      >
                        Send Another Message
                      </button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                            Full Name <span className="text-martimma-red">*</span>
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formState.name}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-xl bg-white/5 border ${
                              errors.name ? 'border-martimma-red' : 'border-white/10'
                            } text-white placeholder-gray-500 focus:outline-none focus:border-martimma-yellow/50 focus:ring-1 focus:ring-martimma-yellow/50 transition-colors`}
                            placeholder="John Doe"
                          />
                          {errors.name && (
                            <p className="mt-1 text-sm text-martimma-red flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> {errors.name}
                            </p>
                          )}
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                            Email Address <span className="text-martimma-red">*</span>
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formState.email}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-xl bg-white/5 border ${
                              errors.email ? 'border-martimma-red' : 'border-white/10'
                            } text-white placeholder-gray-500 focus:outline-none focus:border-martimma-yellow/50 focus:ring-1 focus:ring-martimma-yellow/50 transition-colors`}
                            placeholder="john@company.com"
                          />
                          {errors.email && (
                            <p className="mt-1 text-sm text-martimma-red flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> {errors.email}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                            Company Name
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formState.company}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-martimma-yellow/50 focus:ring-1 focus:ring-martimma-yellow/50 transition-colors"
                            placeholder="Your Company Ltd"
                          />
                        </div>
                        <div>
                          <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                            Service Interest
                          </label>
                          <select
                            id="service"
                            name="service"
                            value={formState.service}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-martimma-yellow/50 focus:ring-1 focus:ring-martimma-yellow/50 transition-colors appearance-none"
                          >
                            <option value="" className="bg-martimma-dark">Select a service</option>
                            <option value="petroleum" className="bg-martimma-dark">Petroleum Products</option>
                            <option value="construction" className="bg-martimma-dark">Construction</option>
                            <option value="agriculture" className="bg-martimma-dark">Agriculture</option>
                            <option value="logistics" className="bg-martimma-dark">Haulage & Logistics</option>
                            <option value="other" className="bg-martimma-dark">Other</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                          Message <span className="text-martimma-red">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formState.message}
                          onChange={handleChange}
                          rows={5}
                          className={`w-full px-4 py-3 rounded-xl bg-white/5 border ${
                            errors.message ? 'border-martimma-red' : 'border-white/10'
                          } text-white placeholder-gray-500 focus:outline-none focus:border-martimma-yellow/50 focus:ring-1 focus:ring-martimma-yellow/50 transition-colors resize-none`}
                          placeholder="Tell us about your project or inquiry..."
                        />
                        {errors.message && (
                          <p className="mt-1 text-sm text-martimma-red flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> {errors.message}
                          </p>
                        )}
                      </div>

                      {errors.form && (
                        <p className="text-sm text-martimma-red flex items-center gap-1">
                          <AlertCircle className="w-4 h-4 shrink-0" /> {errors.form}
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                              className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                            />
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Message
                            <Send className="w-5 h-5" />
                          </>
                        )}
                      </button>

                      <p className="text-gray-500 text-xs text-center">
                        By submitting this form, you agree to our privacy policy. We respect your data.
                      </p>
                    </form>
                  )}
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
